'use client'

import type React from 'react'
import { useState, useCallback } from 'react'
import type { CreateCompanyUseCase } from '../../usecases/CreateCompanyUseCase'
import type { CompanyCreateRequest } from '../../domain/company'
import { CompanyAdapter } from '../../infrastructure/CompanyAdapter'
import { isStrictErrorResponse } from '../../domain/error_validation'

interface FormData extends CompanyCreateRequest {
    commercialName: string
    country: number
    fiscalAddress: string
}

interface UseCompanyFormProps {
    createCompanyUseCase: CreateCompanyUseCase
    onSuccess?: () => void
}

const useCompanyForm = ({
    createCompanyUseCase,
    onSuccess,
}: UseCompanyFormProps) => {
    const [formData, setFormData] = useState<FormData>({
        commercialName: '',
        country: 0,
        fiscalAddress: '',
    })

    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const isFormValid = useCallback(() => {
        return (
            formData.commercialName.trim() !== '' &&
            formData.country &&
            formData.fiscalAddress.trim() !== ''
        )
    }, [formData])

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }))
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (isFormValid() === false) {
            setError('Por favor completa todos los campos obligatorios')
            return null
        }

        setIsLoading(true)
        setError(null)

        try {
            const backendRequest = CompanyAdapter.fromCreateRequest(formData)
            const company = await createCompanyUseCase.execute(backendRequest)
            if (onSuccess) {
                onSuccess()
            }
            return company
        } catch (err: unknown) {
            let errorMessage =
                'Error al crear la empresa. Por favor intenta nuevamente.'

            // Validar si el error tiene la estructura esperada
            if (isStrictErrorResponse(err)) {
                errorMessage = err.response.data.message
            }

            setError(errorMessage)
            throw new Error(errorMessage)
        } finally {
            setIsLoading(false)
        }
    }

    const clearError = () => {
        setError(null)
    }

    return {
        formData,
        handleChange,
        handleSubmit,
        isFormValid: isFormValid(),
        isLoading,
        error,
        clearError,
    }
}

export default useCompanyForm
